<template>
  <div class="sbar-wrapper">
    <div class="sbar-header">
      <img v-if="isDark" src="/images/logo-font-white.svg" alt="Yuzu logo" class="logo" />
      <img v-else-if="!isDark" src="/images/logo-font-black.svg" alt="Yuzu logo" class="logo" />
    </div>
    <transition name="fade" mode="out-in">
      <div class="sbar-body">
        <div class="sbar-separator">{{ $t('app.experience.sidebar.space') }}</div>
        <div class="sub-section">
          <div class="sub-section-context" style="padding-bottom: 0px;">{{ $t('app.experience.sidebar.assessments') }}</div>
          <Item v-for="item in navItems" :key="item.status" :routeName="statusRouteName[item.status]" icon="bi-folder" :label="item.label" :count="counts[item.status]" :active="isItemActive(item.status)"></Item>
        </div>
      </div>
    </transition>
    <div class="sbar-footer">
      <div class="sbar-item tweaks">
        <div class="sbar-separator">{{ $t('app.sidebar.sections.tools') }}</div>
        <div class="tweaks__item" @click="router.push({ name: 'tutorial' })">
          <i class="bi bi-book"></i>
          <span>{{ $t('app.sidebar.items.guide') }}</span>
        </div>
        <ThemeFooter></ThemeFooter>
        <LanguageFooter></LanguageFooter>
      </div>
      <div class="sbar-item profile-wrapper">
        <ProfileFooter data-step="account"></ProfileFooter>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useDark } from '@vueuse/core';

import Item from '@/components/sidebarV2/Item.vue';
import ThemeFooter from '@/components/sidebarV2/ThemeFooter.vue';
import LanguageFooter from '@/components/sidebarV2/LanguageFooter.vue';
import ProfileFooter from '@/components/sidebarV2/ProfileFooter.vue';

import { useCandidateAssessmentsV4 } from '@/queries/assessment.query';
import { AssessmentStatus, getAssessmentStatus, statusRouteName } from '@/views/experience/assessmentStatus';

const isDark = useDark();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();

const { data: assessments } = useCandidateAssessmentsV4();

const navItems = computed<{ status: AssessmentStatus, label: string }[]>(() => [
  { status: 'invited', label: t('app.experience.sidebar.invitations') },
  { status: 'started', label: t('app.experience.sidebar.inProgress') },
  { status: 'finished', label: t('app.experience.sidebar.finished') },
]);

const counts = computed(() => {
  const res: Record<AssessmentStatus, number> = { invited: 0, started: 0, finished: 0 };
  (assessments.value || []).forEach((a) => res[getAssessmentStatus(a)]++);
  return res;
});

// On an assessment page, highlight the list the assessment belongs to
function isItemActive(status: AssessmentStatus) {
  if (route.name === 'assessment') {
    const current = assessments.value?.find((a) => a.campaign_id === route.params.campaignId);
    return !!current && getAssessmentStatus(current) === status;
  }
  return route.name === statusRouteName[status];
}

</script>

<style lang="scss">
@import "@/assets/style/variables.scss";

  .tweaks__item {
    display: flex;
    gap: 6px;
    align-items: center;
    user-select: none;

    padding: 4px 8px;
    border-radius: 8px;
    cursor: pointer;

    &:hover {
      background-color: var(--primary-300-color);
      color: $secondary-color;
    }

    i {
      font-size: 14px;
    }

    span {
      font-weight: 500;
      font-size: 14px;
    }

    &.mobile {
      justify-content: center;

      i {
        font-size: 18px;
      }
    }
  }

  .dropdown__menu {
    display: flex;
    flex-direction: column;
    gap: 8px;

    .dropdown__item {
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
      cursor: pointer;
      user-select: none;
  
      display: flex;
      align-items: center;
      gap: 8px;
  
      padding: 4px 8px;
      border-radius: 4px;
  
      &:hover {
        background-color: var(--primary-300-color);
        color: $secondary-color;
      }
    }
  }
</style>

<style lang="scss" scoped>
@import "@/assets/style/variables.scss";

.line {
  width: 2px;
  margin: 0px 10px;
  height: 26px;
  background-color: var(--light-grey-color);
}

.sbar-wrapper {
  flex-shrink: 0;
  display: flex;
  flex-direction: column;

  background-color: var(--white-color);
  border-right: 1px solid var(--light-grey-color);

  width: 250px;
  height: 100vh;

  &.sbar-closed {
    width: 80px;

    .sbar-header {
      justify-content: center;
    }

    .sbar-body {
      gap: 6px;

      .sbar-separator {
        text-align: center;
        justify-content: center;
        font-size: 12px;
        font-weight: 600;

        margin: 6px 0;

        &::after {
          display: none;
        }
      }

      ::v-deep(.sbar-item) {
        justify-content: center;
        padding: 10px 0px;

        .sbar-item-icon {
          font-size: 20px;
        }
      }
    }
  }

  .sbar-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-bottom: 1px solid var(--light-grey-color);
    padding: 0px 1rem;
    height: 3rem;

    .logo-small {
      width: 32px;
    }

    .logo {
      margin-bottom: -8px;
    }

    .sbar-toggle {
      font-size: 22px;
      padding: 4px;
      border-radius: 4px;

      &:hover {
        background-color: var(--primary-200-color);
      }
    }
  }

  .sbar-body {
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 1rem;

    margin-top: 10px;

    overflow-x: hidden;
    overflow-y: auto;

    &::-webkit-scrollbar{
      display: none;
    }

    .sbar-separator {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      font-weight: 600;
      color: $secondary-200-color;
    }

    .sub-section {
      display: flex;
      flex-direction: column;
      gap: 6px;

      padding-left: 8px;
      margin-left: 15px;
      border-left: 1px solid $secondary-100-color;

      .sub-section-context {
        display: flex;
        align-items: center;
        gap: 8px;
        font-size: 12px;
        font-weight: 600;
        padding-bottom: 2px;
        color: $secondary-200-color;

        text-overflow: ellipsis;
        overflow: hidden;
        white-space: nowrap;
      }
    }
  }

  .sbar-footer {
    display: flex;
    flex-direction: column;
    gap: 16px;

    padding: 0.5rem;
    margin-top: auto;

    .sbar-separator {
      display: flex;
      align-items: center;
      gap: 8px;
      font-size: 12px;
      font-weight: 600;
      color: $secondary-200-color;
      padding-left: 8px;
      padding-bottom: 8px;
    }
  }

  .tweaks {
    display: flex;
    flex-direction: column;
    gap: 3px;
  }

  .profile-wrapper {
    display: flex;
    flex-direction: column;
    gap: 3px;
    padding-top: 8px;

    border-top: 1px solid var(--light-grey-color);
  }
}
</style>